import { useEffect } from 'preact/hooks';
import { useUi } from '../ui/ui-store.ts';

/**
 * The modal stack.
 *
 * Rendered only when something is open, so a closed dialog leaves nothing in
 * the document — which is also why it can no longer print by accident.
 *
 * Every open dialog stays mounted: the one underneath keeps its state while a
 * second asks for something on top of it. Escape and a click on the backdrop
 * close the topmost one only.
 */
export function Modal() {
  const modals = useUi((s) => s.modals);
  const closeModal = useUi((s) => s.closeModal);
  const depth = modals.length;

  // Escape closes, matching every other dialog the user meets.
  useEffect(() => {
    if (depth === 0) return;

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeModal();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [depth, closeModal]);

  // Stop the page behind from scrolling while a dialog is up.
  useEffect(() => {
    if (depth === 0) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [depth]);

  if (depth === 0) return null;

  return (
    <>
      {modals.map((modal, index) => {
        const isTop = index === depth - 1;
        return (
          <div
            key={index}
            class="modalBack"
            role="presentation"
            // Only the topmost dialog is interactive; the ones beneath stay
            // visible for context but must not steal a tap.
            aria-hidden={isTop ? undefined : 'true'}
            style={isTop ? undefined : { pointerEvents: 'none' }}
            onClick={(e) => {
              if (isTop && e.target === e.currentTarget) closeModal();
            }}
          >
            <div class="modal" role="dialog" aria-modal="true" aria-label={modal.title}>
              <div class="modalHead">
                <h2>{modal.title}</h2>
                <button class="iconBtn" onClick={closeModal} aria-label="Fermer">
                  ×
                </button>
              </div>
              <div>{modal.body}</div>
            </div>
          </div>
        );
      })}
    </>
  );
}
